/**
 * One `GET`, one region of a screen, three outcomes.
 *
 * WP-K2's two screens each make four or five independent reads, and the reason they
 * are independent is `PRODUCT.md` §4: a single page-level spinner would hold four
 * answers behind the slowest, and a single page-level error would report one
 * refusal as though the whole screen were broken. `web/shapes/my-activity.md` §5
 * makes "skeleton per region" an acceptance criterion, and this hook is what makes
 * it cheap enough that no screen is tempted to do otherwise.
 *
 * ── Why the state is a union and not three booleans ─────────────────────────
 *
 * `loading` / `ok` / `error` are mutually exclusive facts, and three booleans admit
 * five states that cannot happen plus one — all false — that reads as "no data" and
 * is really "nothing was asked". That is the same class of mistake as rendering an
 * outage as a zero, one level down.
 *
 * ── Aborting ────────────────────────────────────────────────────────────────
 *
 * A superseded request (the date changed, the screen unmounted) is aborted, and its
 * `AbortError` is swallowed rather than reported: `api/client.ts` re-throws
 * `AbortError` unchanged for exactly this reason, so an abort must never become an
 * outage on screen. A response that arrives after its own abort is dropped by the
 * generation check, because `AbortController` cannot un-resolve a promise that had
 * already settled when `abort()` was called.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { WorkforceApiError } from './errors';
import { useSession } from '../auth/SessionProvider';

export type QueryStatus = 'loading' | 'ok' | 'error';

export interface QueryResult<T> {
  readonly status: QueryStatus;
  /** Present exactly when `status === 'ok'`. */
  readonly data: T | null;
  /** Present exactly when `status === 'error'`. */
  readonly error: WorkforceApiError | null;
  /**
   * The `ETag` this read carried, unquoted, or `null` when it carried none.
   *
   * This is the **only** place a precondition may come from (WP-L5, Task 53):
   * API_CONTRACT §6.9 makes the incident ETag `updated_at` rendered by PostgreSQL
   * to the microsecond, and reconstructing it from the body would mean a
   * JavaScript `Date` and three lost digits. `null` means "the response had none",
   * never "this resource has no version".
   */
  readonly etag: string | null;
  /** Re-runs the request. What `RetryAction` is wired to. */
  readonly reload: () => void;
}

/**
 * Options a read may carry beyond its path.
 *
 * `headers` exists for `X-Access-Reason`, which is **mandatory in v1** on every
 * sensitive-access read — `GET /alerts`, all five incident operations,
 * `GET /activities/websites` — at least 10 characters after trimming, or `403
 * WORKFORCE_ACCESS_REASON_REQUIRED`. It is a request header rather than a query
 * parameter, so it cannot travel in `path`, which is what every other read varies.
 *
 * It participates in the effect's dependencies through a serialisation rather than
 * by identity, so a caller may build the object inline without re-firing the read
 * on every render.
 */
export interface QueryOptions {
  readonly headers?: Readonly<Record<string, string>>;
}

/**
 * `path === null` means "there is nothing to ask yet" — an empty search box, a
 * screen whose employee id has not resolved. It is **not** an error and **not** an
 * empty result: the hook parks in `loading` with no request in flight, and the
 * caller decides what to render. Collapsing it into an empty `data` would be the
 * absence-as-zero mistake again.
 */
/**
 * `reloadToken` re-runs the request when it changes — the timer's `version`, bumped
 * on every successful mutation, so the entries list re-reads exactly when a timer
 * stopped and not on a poll. It is a **parameter** rather than a cache-busting query
 * value on purpose: a made-up query parameter would travel to the server, where the
 * list endpoints parse strictly, and "the client refreshed" is not a fact the server
 * should have to have an opinion about.
 */
export function useApiQuery<T>(
  path: string | null,
  reloadToken = 0,
  options: QueryOptions = {},
): QueryResult<T> {
  const { client } = useSession();
  const [status, setStatus] = useState<QueryStatus>('loading');
  const [data, setData] = useState<T | null>(null);
  const [etag, setEtag] = useState<string | null>(null);
  const [error, setError] = useState<WorkforceApiError | null>(null);
  const [nonce, setNonce] = useState(0);
  const generation = useRef(0);

  // Serialised rather than depended on by identity: `{ 'X-Access-Reason': reason }`
  // written at the call site is a new object every render, and depending on it
  // would re-issue a sensitive-access read — and write a `wp_data_access_logs` row —
  // on every keystroke anywhere on the screen.
  const headerKey = JSON.stringify(options.headers ?? {});

  useEffect(() => {
    if (path === null) return;
    const controller = new AbortController();
    generation.current += 1;
    const mine = generation.current;
    setStatus('loading');

    const run = async (): Promise<void> => {
      try {
        const headers = JSON.parse(headerKey) as Record<string, string>;
        const response = await client.requestWithHeaders<T>(path, {
          signal: controller.signal,
          headers,
        });
        if (generation.current !== mine) return;
        setData(response.body);
        setEtag(response.etag);
        setError(null);
        setStatus('ok');
      } catch (cause) {
        if (generation.current !== mine) return;
        // The caller's own doing. `client.request` re-throws this untouched so that
        // it can be told apart from a network failure here, which is the whole
        // point of it not being wrapped there.
        if (cause instanceof DOMException && cause.name === 'AbortError') return;
        setError(
          cause instanceof WorkforceApiError
            ? cause
            : new WorkforceApiError({
                code: 'CLIENT_TRANSPORT_FAILED',
                message: cause instanceof Error ? cause.message : 'request failed',
                status: 0,
              }),
        );
        setData(null);
        // A failed read holds no precondition. Keeping the previous one would let a
        // write go out carrying the ETag of a record it did not just read.
        setEtag(null);
        setStatus('error');
      }
    };

    void run();
    return () => {
      controller.abort();
    };
  }, [client, path, nonce, reloadToken, headerKey]);

  const reload = useCallback(() => {
    setNonce((value) => value + 1);
  }, []);

  return { status, data, error, etag, reload };
}
